import React from 'react'
import { Routes, Route, Link } from 'react-router-dom'
import { Server, Code, Mail, Phone, MapPin, Menu, X, ChevronRight, Zap, Globe, MessageSquare, Edit3, Package, Puzzle, LifeBuoy, CheckCircle, Sun, Moon } from 'lucide-react'

// Header Component
function Header() {
  const [isOpen, setIsOpen] = React.useState(false)
  const [darkMode, setDarkMode] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('darkMode') === 'true' || 
        (!localStorage.getItem('darkMode') && window.matchMedia('(prefers-color-scheme: dark)').matches)
    }
    return false
  })

  React.useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('darkMode', 'true')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('darkMode', 'false')
    }
  }, [darkMode])
  
  return (
    <header className="bg-white dark:bg-dark-800 shadow-sm sticky top-0 z-50 transition-colors duration-300">
      <nav className="container-custom">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 dark:text-white">Mawi Labs</span>
          </Link>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Home</Link>
            <Link to="/products" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Products</Link>
            <Link to="/services" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Services</Link>
            <Link to="/pricing" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Pricing</Link>
            <Link to="/docs" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Docs</Link>
            <Link to="/about" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="btn btn-primary">Contact Us</Link>
            <button 
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" /> : <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />}
            </button>
          </div>
          
            <button 
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors md:hidden"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" /> : <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />}
            </button>
            
            <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
        </div>
        
        {isOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col space-y-3">
              <Link to="/" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" onClick={() => setIsOpen(false)}>Home</Link>
              <Link to="/products" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" onClick={() => setIsOpen(false)}>Products</Link>
              <Link to="/services" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" onClick={() => setIsOpen(false)}>Services</Link>
              <Link to="/pricing" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" onClick={() => setIsOpen(false)}>Pricing</Link>
              <Link to="/docs" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" onClick={() => setIsOpen(false)}>Docs</Link>
              <Link to="/about" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" onClick={() => setIsOpen(false)}>About</Link>
              <Link to="/contact" className="btn btn-primary text-center" onClick={() => setIsOpen(false)}>Contact Us</Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}

// Footer Component
function Footer() {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Mawi Labs</span>
            </div>
            <p className="text-gray-400 text-sm">
              Empowering businesses with cutting-edge IT infrastructure and server management solutions.
            </p>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-4">Products</h3>
            <ul className="space-y-2">
              <li><Link to="/products#mawi-stack" className="hover:text-white transition-colors">Mawi Server Stack</Link></li>
              <li><Link to="/products#mawi-cms" className="hover:text-white transition-colors">Mawi CMS</Link></li>
              <li><Link to="/products#mawi-chat" className="hover:text-white transition-colors">Mawi Helpdesk</Link></li>
              <li><Link to="/products#mawi-code-editor" className="hover:text-white transition-colors">Mawi Code Builder</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              <li><Link to="/services#custom-development" className="hover:text-white transition-colors">Custom Development</Link></li>
              <li><Link to="/services#it-helpdesk" className="hover:text-white transition-colors">IT Helpdesk</Link></li>
              <li><Link to="/services#server-management" className="hover:text-white transition-colors">Server Management</Link></li>
              <li><Link to="/services#consulting" className="hover:text-white transition-colors">IT Consulting</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>amer@mawi.uk</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>+44 7946416555</span>
              </li>
              <li className="flex items-center space-x-2">
                <Globe className="w-4 h-4" />
                <span>web.mawi.uk</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; 2024 Mawi Labs. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

// Home Page
function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container-custom py-20">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6 text-white">Power Your Business with Modern IT Infrastructure</h1>
            <p className="text-xl mb-8 text-blue-100">
              Mawi Labs delivers cutting-edge server management, custom business applications, and comprehensive IT support to help your business thrive in the digital age.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn bg-white text-blue-600 hover:bg-blue-50">
                Get Started
              </Link>
              <Link to="/products" className="btn border-2 border-white text-white hover:bg-white hover:text-blue-600">
                View Products
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-20 section-alt">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Products</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Innovative solutions designed to streamline your operations and boost productivity
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/products#mawi-stack" className="card p-6 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Mawi Server Stack</h3>
              <p className="text-gray-600 text-sm">Complete server & network management platform</p>
            </Link>
            
            <Link to="/products#mawi-cms" className="card p-6 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Mawi CMS</h3>
              <p className="text-gray-600 text-sm">Modern content management system, built for modern businesses</p>
            </Link>
            
            <Link to="/products#mawi-chat" className="card p-6 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Mawi Helpdesk</h3>
              <p className="text-gray-600 text-sm">AI-powered Helpdesk application</p>
            </Link>
            
            <Link to="/products#mawi-code-editor" className="card p-6 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Edit3 className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Mawi Code Builder</h3>
              <p className="text-gray-600 text-sm">AI-assisted development environment</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive IT solutions tailored to your business needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card p-8 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Server Management</h3>
              <p className="text-gray-600">
                Professional server administration, monitoring, and optimization services for maximum uptime and performance.
              </p>
            </div>
            
            <div className="card p-8 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Custom Development</h3>
              <p className="text-gray-600">
                Tailored software solutions designed to streamline your operations and drive business growth.
              </p>
            </div>
            
            <div className="card p-8 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <LifeBuoy className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">IT Helpdesk</h3>
              <p className="text-gray-600">
                24/7 technical support and consulting services to keep your business running smoothly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-900 text-white py-20">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-4 text-white">Ready to Transform Your IT Infrastructure?</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses that trust Mawi Labs for their IT needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn btn-primary inline-flex items-center">
              Contact Us Today
              <ChevronRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/pricing" className="btn border-2 border-white text-white hover:bg-white hover:text-gray-900">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

// Products Page
function Products() {
  const products = [
    {
      id: "mawi-stack",
      name: "Mawi Server Stack",
      icon: Server,
      tagline: "Complete Server Management Platform",
      description: "A comprehensive microservices-based platform for managing your entire server infrastructure. Monitor, deploy, and scale your applications with ease. Trusted by 500+ enterprise customers.",
      price: "£149 / month",
      technicalSpecs: [
        "Linux/Windows Server Support",
        "REST + GraphQL APIs",
        "Docker & Kubernetes Integration",
        "PostgreSQL Database",
        "Node.js 22 Runtime",
        "Horizontal Scaling Support"
      ],
      integrations: [
        "AWS", "Azure", "Google Cloud", "DigitalOcean", "Docker Hub", "GitLab CI"
      ],
      features: [
        "Real-time system monitoring",
        "Docker container management",
        "SSH connection management",
        "AI-powered assistance",
        "Plugin architecture",
        "Multi-user support",
        "Automated backups",
        "Security audit logging"
      ],
      color: "blue",
      rating: 4.9,
      reviews: 327
    },
    {
      id: "mawi-cms",
      name: "Mawi CMS",
      icon: Package,
      tagline: "Modern Content Management System",
      description: "A headless CMS built for modern web applications. Create, manage, and deliver content across multiple platforms with our flexible API.",
      price: "£79 / month",
      technicalSpecs: [
        "Headless GraphQL API",
        "PostgreSQL + Redis",
        "Webhook System",
        "CDN Ready",
        "Multi-tenant Support",
        "SSO Authentication"
      ],
      integrations: [
        "Next.js", "React", "Vue", "Nuxt", "Vercel", "Cloudflare"
      ],
      features: [
        "Headless architecture",
        "GraphQL & REST API",
        "Multi-language support",
        "Media library management",
        "Role-based permissions",
        "SEO optimization tools"
      ],
      color: "purple",
      rating: 4.7,
      reviews: 189
    },
    {
      id: "mawi-chat",
      name: "Mawi Helpdesk",
      icon: MessageSquare,
      tagline: "AI-Powered Chat Application",
      description: "A production-ready web chat application with support for both local and cloud AI models. Perfect for customer support, internal communications, or as a standalone product.",
      price: "£59 / month",
      technicalSpecs: [
        "WebSocket Real-time",
        "Ollama / OpenAI / Anthropic",
        "Vector Database Built-in",
        "RAG Implementation",
        "OAuth 2.0 Support",
        "Multi-channel Support"
      ],
      integrations: [
        "Slack", "Teams", "Discord", "WhatsApp", "Zendesk", "Intercom"
      ],
      features: [
        "User authentication",
        "Conversation management",
        "Multi-model AI support",
        "Real-time messaging",
        "Customizable interface",
        "API integration",
        "Ticket Management",
        "Knowledge Base Integration"
      ],
      color: "green",
      rating: 4.8,
      reviews: 241
    },
    {
      id: "mawi-code-editor",
      name: "Mawi Code Builder",
      icon: Edit3,
      tagline: "AI-Assisted Development Environment",
      description: "An intelligent code editor with AI-powered code completion, refactoring suggestions, and documentation generation. Coming soon!",
      features: [
        "AI code completion",
        "Smart refactoring",
        "Documentation generation",
        "Multi-language support",
        "Integrated terminal",
        "Git integration"
      ],
      color: "orange",
      badge: "Coming Soon"
    },
    {
      id: "vs-code-extension",
      name: "IDE Plugin",
      icon: Puzzle,
      tagline: "Mawi Labs Integration for VS Code",
      description: "Bring Mawi Labs functionality directly to your VS Code editor. Manage servers, deploy applications, and access AI assistance without leaving your development environment.",
      features: [
        "Server management from VS Code",
        "One-click deployments",
        "Integrated AI chat",
        "Remote file editing",
        "Terminal integration",
        "Snippet library"
      ],
      color: "indigo"
    }
  ]

  return (
    <div className="py-20">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">Our Products</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Innovative solutions designed to streamline your operations and boost productivity
          </p>
        </div>
        
        <div className="space-y-16">
          {products.map((product) => (
            <div key={product.id} id={product.id} className="scroll-mt-20">
      <div className="card shadow-lg overflow-hidden rounded-2xl">
                <div className="grid md:grid-cols-2">
                  <div className={`p-8 md:p-12 bg-${product.color}-50 flex items-center justify-center`}>
                  <div className={`w-32 h-32 bg-${product.color}-100 rounded-2xl flex items-center justify-center`}>
                      <product.icon className={`w-16 h-16 text-${product.color}-600`} />
                    </div>
                  </div>
                  <div className="p-8 md:p-12">
                    <div className="flex items-center gap-3 mb-4">
                      <h2 className="text-3xl font-bold">{product.name}</h2>
                      {product.badge && (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">
                          {product.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-lg text-gray-600 mb-6">{product.tagline}</p>
                    <p className="text-gray-600 mb-4">{product.description}</p>

                    {product.price && (
                      <div className="mb-6">
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">{product.price}</span>
                      </div>
                    )}

                    {product.technicalSpecs && (
                      <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-600 rounded-lg">
                        <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">Technical Specifications</h4>
                        <ul className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                          {product.technicalSpecs.map((spec, i) => (
                            <li key={i} className="flex items-center">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                              {spec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {product.integrations && (
                      <div className="mb-6">
                        <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">Integrations</h4>
                        <div className="flex flex-wrap gap-2">
                          {product.integrations.map((integration, i) => (
                            <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-dark-600 text-gray-700 dark:text-gray-300 text-sm rounded-full">
                              {integration}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <ul className="space-y-3 mb-8">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-center">
                          <CheckCircle className={`w-5 h-5 text-${product.color}-500 mr-3 flex-shrink-0`} />
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" className={`btn btn-primary inline-flex items-center`}>
                      Learn More
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Services Page
function Services() {
  const services = [
    {
      id: "custom-development",
      name: "Custom Software Development",
      icon: Code,
      description: "Tailored software solutions built to your exact specifications. From web applications to enterprise systems, we deliver high-quality code that drives business value.",
      features: [
        "Web application development",
        "Mobile app development",
        "API design and development",
        "Database design and optimization",
        "Legacy system modernization",
        "Third-party integrations"
      ]
    },
    {
      id: "it-helpdesk",
      name: "IT Helpdesk Support",
      icon: LifeBuoy,
      description: "Round-the-clock technical support to keep your business running smoothly. Our expert team is ready to assist with any IT issues, big or small.",
      features: [
        "24/7 phone and email support",
        "Remote troubleshooting",
        "Incident management",
        "Problem resolution",
        "Knowledge base access",
        "Priority support options"
      ]
    },
    {
      id: "server-management",
      name: "Server Management",
      icon: Server,
      description: "Professional server administration services ensuring maximum uptime, security, and performance for your critical infrastructure.",
      features: [
        "Server monitoring and alerts",
        "Security patching and updates",
        "Backup and disaster recovery",
        "Performance optimization",
        "Capacity planning",
        "Multi-cloud management"
      ]
    },
    {
      id: "consulting",
      name: "IT Consulting",
      icon: Zap,
      description: "Strategic technology consulting to help you make informed decisions and optimize your IT investments for maximum ROI.",
      features: [
        "Technology assessment",
        "Architecture design",
        "Cloud migration strategy",
        "Security audits",
        "Cost optimization",
        "Digital transformation"
      ]
    }
  ]

  return (
    <div className="py-20">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">Our Services</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Comprehensive IT solutions tailored to your business needs
          </p>
        </div>
        
        <div className="space-y-16">
          {services.map((service) => (
            <div key={service.id} id={service.id} className="scroll-mt-20">
              <div className="card shadow-lg overflow-hidden rounded-2xl">
                <div className="grid md:grid-cols-2">
      <div className="p-8 md:p-12 bg-gray-50 dark:bg-dark-600 flex items-center justify-center transition-colors duration-300">
                    <div className="w-32 h-32 bg-blue-100 rounded-2xl flex items-center justify-center">
                      <service.icon className="w-16 h-16 text-blue-600" />
                    </div>
                  </div>
                  <div className="p-8 md:p-12">
                    <h2 className="text-3xl font-bold mb-4">{service.name}</h2>
                    <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" className="btn btn-primary inline-flex items-center">
                      Get Started
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// IT Support Packages
function SupportPackages() {
  const packages = [
    {
      name: "Basic Support",
      price: "£49",
      period: "/month",
      description: "Essential support for small businesses",
      features: [
        "Email support (9am-5pm Mon-Fri)",
        "4-hour response time",
        "Remote troubleshooting",
        "Knowledge base access",
        "Monthly health check",
        "Up to 5 users"
      ],
      popular: false
    },
    {
      name: "Professional Support",
      price: "£99",
      period: "/month",
      description: "Comprehensive support for growing businesses",
      features: [
        "24/7 phone & email support",
        "1-hour response time",
        "Remote troubleshooting",
        "On-site support (4 hours/month)",
        "Priority ticket handling",
        "Weekly health checks",
        "Up to 25 users",
        "Server monitoring"
      ],
      popular: true
    },
    {
      name: "Enterprise Support",
      price: "£299",
      period: "/month",
      description: "Full-service support for large organizations",
      features: [
        "24/7 dedicated support team",
        "15-minute response time",
        "Unlimited on-site support",
        "Dedicated account manager",
        "Custom SLA agreements",
        "24/7 server monitoring",
        "Unlimited users",
        "Quarterly business reviews",
        "Disaster recovery support"
      ],
      popular: false
    }
  ]

  return (
      <div className="py-20 section-alt">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">IT Support Packages</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose the support package that best fits your business needs
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {packages.map((pkg, index) => (
            <div key={index} className={`p-8 rounded-2xl ${pkg.popular ? 'bg-blue-600 text-white shadow-xl scale-105' : 'card shadow-lg'}`}>
              {pkg.popular && (
                <div className="bg-yellow-400 text-yellow-900 text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                  Most Popular
                </div>
              )}
              <h3 className={`text-2xl font-bold mb-2 ${pkg.popular ? 'text-white' : 'dark:text-white'}`}>{pkg.name}</h3>
              <p className={`mb-4 ${pkg.popular ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>{pkg.description}</p>
              <div className="mb-6">
                <span className={`text-4xl font-bold ${pkg.popular ? 'text-white' : ''}`}>{pkg.price}</span>
                <span className={pkg.popular ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}>{pkg.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <svg className={`w-5 h-5 mr-2 ${pkg.popular ? 'text-blue-200' : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className={pkg.popular ? 'text-blue-100' : 'text-gray-600 dark:text-white'}>{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className={`w-full btn ${pkg.popular ? 'bg-white text-blue-600 hover:bg-blue-50' : 'btn-primary'}`}>
                Choose Plan
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Docs Page
function Docs() {
  return (
    <div className="py-20">
      <div className="container-custom">
        <h1 className="text-4xl font-bold mb-8">Documentation</h1>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <nav className="section-alt rounded-lg p-6 sticky top-24">
              <h2 className="font-semibold mb-4">Contents</h2>
              <ul className="space-y-2">
                <li><a href="#getting-started" className="text-blue-600 hover:underline">Getting Started</a></li>
                <li><a href="#architecture" className="text-gray-600 hover:underline">Architecture</a></li>
                <li><a href="#api-reference" className="text-gray-600 hover:underline">API Reference</a></li>
                <li><a href="#tutorials" className="text-gray-600 hover:underline">Tutorials</a></li>
              </ul>
            </nav>
          </div>
          <div className="md:col-span-2">
            <div className="prose max-w-none">
              <section id="getting-started" className="mb-12">
                <h2 className="text-2xl font-bold mb-4">Getting Started</h2>
                <p className="text-gray-600 mb-4">
                  Welcome to Mawi Labs documentation. This guide will help you get started with our platform and services.
                </p>
                <h3 className="text-xl font-semibold mb-2">Prerequisites</h3>
                <ul className="list-disc list-inside text-gray-600 mb-4">
                  <li>Basic understanding of IT infrastructure</li>
                  <li>Access to server management tools</li>
                  <li>Active Mawi Labs account</li>
                </ul>
              </section>
              
              <section id="architecture" className="mb-12">
                <h2 className="text-2xl font-bold mb-4">Architecture</h2>
                <p className="text-gray-600">
                  Our platform is built on a modern microservices architecture, ensuring scalability, reliability, and security.
                </p>
              </section>
              
              <section id="api-reference">
                <h2 className="text-2xl font-bold mb-4">API Reference</h2>
                <p className="text-gray-600">
                  Comprehensive API documentation for developers looking to integrate with Mawi Labs services.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// About Page
function About() {
  return (
    <div className="py-20">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 text-center">About Mawi Labs</h1>
          
          <div className="prose max-w-none">
            <p className="text-xl text-gray-600 mb-8">
              Mawi Labs is a leading provider of IT infrastructure and server management solutions. We help businesses of all sizes optimize their technology stack and achieve their digital transformation goals.
            </p>
            
            <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
            <p className="text-gray-600 mb-8">
              To empower businesses with reliable, scalable, and secure IT infrastructure that drives growth and innovation.
            </p>
            
            <h2 className="text-2xl font-bold mb-4">Our Values</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="section-alt rounded-lg p-4">
                <h3 className="font-semibold mb-2">Excellence</h3>
                <p className="text-gray-600 text-sm">We strive for excellence in everything we do.</p>
              </div>
            <div className="section-alt rounded-lg p-4">
                <h3 className="font-semibold mb-2">Innovation</h3>
                <p className="text-gray-600 text-sm">We embrace new technologies and approaches.</p>
              </div>
              <div className="section-alt rounded-lg p-4">
                <h3 className="font-semibold mb-2">Integrity</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">We operate with honesty and transparency.</p>
              </div>
              <div className="section-alt rounded-lg p-4">
                <h3 className="font-semibold mb-2">Customer Focus</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Our customers' success is our priority.</p>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold mb-4">Contact Information</h2>
            <div className="space-y-2 text-gray-600">
              <p><strong>Email:</strong> amer@mawi.uk</p>
              <p><strong>Phone:</strong> +44 7946416555</p>
              <p><strong>Website:</strong> web.mawi.uk</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Contact Page
function Contact() {
  return (
    <div className="py-20">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 text-center">Contact Us</h1>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold mb-4">Get in Touch</h2>
              <p className="text-gray-600 mb-8">
                Have a question or want to learn more about our services? We'd love to hear from you.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-gray-600">amer@mawi.uk</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Phone</p>
                    <p className="text-gray-600">+44 7946416555</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Location</p>
                    <p className="text-gray-600">United Kingdom</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input type="text" className="w-full px-4 py-2 input-field border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" className="w-full px-4 py-2 input-field border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="your@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subject</label>
                  <input type="text" className="w-full px-4 py-2 input-field border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="How can we help?" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Message</label>
                  <textarea rows={4} className="w-full px-4 py-2 input-field border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Your message..."></textarea>
                </div>
                <button type="submit" className="w-full btn btn-primary">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Pricing Page (Updated to include support packages link)
function Pricing() {
  return (
    <div>
      <div className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold mb-4">Software Licensing</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose the plan that best fits your business needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div className="card p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-2">Starter</h3>
              <p className="text-gray-500 mb-4">For small teams</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">£39</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">Basic server management</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">Email support</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">99% uptime SLA</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">Up to 5 servers</span>
                </li>
              </ul>
              <Link to="/contact" className="w-full btn btn-primary">Get Started</Link>
            </div>
            
            <div className="p-8 rounded-xl bg-blue-600 text-white shadow-xl scale-105">
              <div className="bg-yellow-400 text-yellow-900 text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">Most Popular</div>
              <h3 className="text-2xl font-bold mb-2">Professional</h3>
              <p className="text-blue-100 mb-4">For growing businesses</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">£99</span>
                <span className="text-blue-200">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-blue-200 mr-2" />
                  <span>Advanced server management</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-blue-200 mr-2" />
                  <span>24/7 phone support</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-blue-200 mr-2" />
                  <span>99.9% uptime SLA</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-blue-200 mr-2" />
                  <span>Unlimited servers</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-blue-200 mr-2" />
                  <span>Custom applications</span>
                </li>
              </ul>
              <Link to="/contact" className="w-full btn bg-white text-blue-600 hover:bg-blue-50">Get Started</Link>
            </div>
            
            <div className="card p-8 shadow-lg rounded-xl">
              <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
              <p className="text-gray-500 mb-4">For large organizations</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">Custom</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">Full infrastructure management</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">Dedicated support team</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">99.99% uptime SLA</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">Custom solutions</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-gray-600 dark:text-white">On-premise options</span>
                </li>
              </ul>
              <Link to="/contact" className="w-full btn btn-primary">Contact Sales</Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* IT Support Packages Section */}
      <SupportPackages />
    </div>
  )
}

// Main App
function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/services" element={<Services />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </main>
      <Footer />
    </div>
  )
}

export default App